import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Clock, Download, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import Spinner from '@/components/ui/Spinner';
import StatusPill from '@/components/ui/StatusPill';
import type { Production } from '@/hooks/useProject';
import { apiFetch } from '@/lib/api';
import { stageLabel } from '@/lib/labels';

/** Every run of this project, newest last, with download and delete. */
export default function ProductionsList({
  projectId,
  productions,
  onDeleted,
}: {
  projectId: string;
  productions: Production[];
  onDeleted: (id: string) => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation(['dashboard', 'common']);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Production | null>(null);

  const download = async (prodId: string) => {
    setDownloading(prodId);
    try {
      const resp = await apiFetch(`/api/download/${prodId}`);
      if (resp.ok) {
        const data = await resp.json();
        if (data.url) window.open(data.url, '_blank');
      }
    } catch {
      /* best effort */
    } finally {
      setDownloading(null);
    }
  };

  const remove = async (prodId: string) => {
    try {
      await apiFetch(`/api/production/${prodId}`, { method: 'DELETE' });
      onDeleted(prodId);
    } catch {
      /* best effort */
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Clock className="h-4 w-4" />
          {t('productions.title')}
        </CardTitle>
        <CardDescription>{t('productions.total', { count: productions.length })}</CardDescription>
      </CardHeader>
      <CardContent>
        {productions.length === 0 && <p className="py-4 text-center text-sm text-faint">{t('productions.empty')}</p>}
        <div className="space-y-2">
          {productions.map((prod) => {
            const finished = ['complete', 'error', 'cancelled'].includes(prod.status);
            return (
              <div
                key={prod.id}
                className="flex cursor-pointer items-center gap-2 rounded-lg border border-border p-2 transition-colors hover:bg-muted/40"
                onClick={() => navigate(`/projects/${projectId}/productions/${prod.id}`)}
              >
                <StatusPill status={prod.status}>{prod.status}</StatusPill>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs text-muted-foreground">{stageLabel(t, prod.phase)}</div>
                  <div className="truncate text-[11px] text-faint">
                    {prod.started_at ? new Date(prod.started_at).toLocaleString() : '—'}
                  </div>
                </div>
                {prod.status === 'running' && <Spinner />}
                {finished && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2"
                    title={t('productions.downloadTitle')}
                    disabled={downloading === prod.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      download(prod.id);
                    }}
                  >
                    {downloading === prod.id ? <Spinner /> : <Download className="h-3 w-3" />}
                  </Button>
                )}
                {prod.status !== 'running' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-destructive hover:text-destructive"
                    title={t('productions.deleteTitle')}
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDelete(prod);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
                <ArrowRight className="h-4 w-4 shrink-0 text-faint" />
              </div>
            );
          })}
        </div>
      </CardContent>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={t('productions.deleteConfirmTitle', { id: confirmDelete?.id.slice(-6) ?? '' })}
        warning={t('productions.deleteWarning')}
        destructive
        confirmLabel={t('productions.deleteConfirm')}
        onConfirm={() => confirmDelete && remove(confirmDelete.id)}
      />
    </Card>
  );
}
